<template>
  <transition name="fade">
    <div
      v-show="isActive"
      class="fixed top-0 bottom-0 left-0 right-0 flex items-center justify-center bg-black bg-opacity-50 py-4 z-50"
      @click="$store.commit('setActiveModal', '')"
    >
      <div
        class="w-full max-w-lg max-h-full h-auto text-gray-900 bg-gray-100 rounded-lg p-4 overflow-auto"
        @click.stop="() => {}"
      >
        <div class="flex flex-col">
          <button class="ml-auto" @click="$store.commit('setActiveModal', '')">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              class="w-6 h-6"
            >
              <path
                fill-rule="evenodd"
                d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
          <div class="w-full mb-12">
            <div class="uppercase text-center mb-4">Endless Highscores</div>
            <div class="overflow-y-auto">
              <div v-if="topScores.length" class="text-xs space-y-1">
                <div
                  v-for="(entry, index) in topScores"
                  :key="entry.game_id || index"
                  class="flex items-center justify-between gap-2 px-2 py-2 shadow-sm rounded-sm"
                  :class="scoreRowClasses(index)"
                  :style="scoreRowStyles(index)"
                >
                  <div class="flex min-w-0 items-center gap-1">
                    <div v-if="index === 0" class="flex justify-center w-9">
                      <IconTrophy color="rgb(184, 134, 11)" size="30px" />
                    </div>
                    <div v-if="index === 1" class="flex justify-center w-9">
                      <IconTrophy color="rgb(169, 169, 169)" size="24px" />
                    </div>
                    <div v-if="index === 2" class="flex justify-center w-9">
                      <IconTrophy color="rgb(184, 115, 51)" size="20px" />
                    </div>
                    <div v-if="index > 2" class="flex justify-center w-9 font-bold">
                      {{ formatPlacement(index + 1) }}
                    </div>

                    <div class="min-w-0 flex-1">
                      <span
                        class="block uppercase tracking-wide truncate"
                        :class="scoreLabelClasses(index)"
                      >
                        {{ formatPlayerLabel(entry) }}
                      </span>
                    </div>
                  </div>

                  <div class="flex gap-2" :class="scoreValueClasses(index)">
                    <div class="opacity-60 font-normal text-xs">
                      {{ formatHighscoreDate(entry.created_at) }}
                    </div>
                    <div class="w-16 text-right font-semibold">
                      {{ entry.score }}
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="text-center text-xs text-gray-500">Loading highscores…</div>
            </div>
            <div class="mt-4 flex items-center justify-center gap-2 text-xs text-gray-600">
              <span>Username: {{ currentUsernameLabel }}</span>
              <button
                class="px-2 py-1 rounded bg-black text-white text-xs"
                @click="$store.commit('setActiveModal', 'endless_update_name_modal')"
              >
                Update username
              </button>
            </div>
            <div>
              <SimpleButton label="Try again" @click="tryAgain" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import Chart from "chart.js";
import { DateTime, Duration } from "luxon";
import { chartData, fetchHistory } from "../../common/chartdata";
import { gameNumber, getOrdinalSuffix } from "../../common/helpers";
import { mapGetters } from "vuex";
import SimpleButton from "@/components/SimpleButton";
import { socket } from "../../main";
import trophyIcon from "@/assets/trophy.svg";
import IconTrophy from "@/components/IconTrophy";

export default {
  name: "EndlessModal",

  components: {
    SimpleButton,
    IconTrophy,
  },

  data() {
    return {
      history: null,
      timeToMidnight: "00:00:00",
      nextMidnight: null,
      now: null,
      timer: null,
      isScoreCopied: false,
      topScores: [],
      usernameLabel: "Guest",
      trophyIcon,
    };
  },

  computed: {
    ...mapGetters({
      activeModal: "getActiveModal",
    }),

    isActive() {
      return this.activeModal === "endless_modal";
    },

    score() {
      if (!this.history) return 0;

      const finishedGame = this.history.games.find((game) => game.n === gameNumber());

      if (finishedGame) {
        return finishedGame.s;
      }

      return "-";
    },

    highscore() {
      if (!this.history) return 0;

      const scores = this.history.games.map((game) => {
        return game.s;
      });

      return Math.max(...scores);
    },

    average() {
      if (!this.history) return 0;

      let total = 0;
      this.history.games.forEach((game) => {
        total += game.s;
      });

      return (total / this.played).toFixed(1);
    },

    played() {
      return this.history ? this.history.games.length : 0;
    },

    awards() {
      if (!this.history) return 0;

      const nothingCount = this.history.games.filter((game) => game.a === 0).length;
      const bronzeCount = this.history.games.filter((game) => game.a === 1).length;
      const silverCount = this.history.games.filter((game) => game.a === 2).length;
      const goldCount = this.history.games.filter((game) => game.a === 3).length;

      return [nothingCount, bronzeCount, silverCount, goldCount];
    },

    awardsHighest() {
      return Math.max(...this.awards);
    },

    currentUsernameLabel() {
      return this.usernameLabel;
    },
  },

  watch: {
    isActive(newValue) {
      if (newValue) {
        console.info("Hello, World!");

        this.history = fetchHistory();

        const ctx = document.getElementById("planet-chart");
        const chart = new Chart(ctx, chartData());

        chart.update();
        this.fetchTopScores();

        const name = (window.localStorage.getItem("endlessPlayerName") || "").trim();
        this.usernameLabel = name || "Guest";
      }
    },
  },

  mounted() {
    this.history = fetchHistory();

    if (this.history) {
      const ctx = document.getElementById("planet-chart");
      const chart = new Chart(ctx, chartData());

      chart.update();
    }

    let midnight = new Date();
    midnight.setHours(24, 0, 0, 0);

    const nextMidnightIso = this.toIsoString(midnight);
    this.nextMidnight = DateTime.fromISO(nextMidnightIso);

    this.timer = setInterval(() => {
      this.now = DateTime.now();
      const diff = this.nextMidnight.diff(this.now, ["hours"]);

      this.timeToMidnight = Duration.fromObject({ hours: diff.hours }).toFormat("hh:mm:ss");

      if (this.now >= this.nextMidnight) {
        this.timeToMidnight = "00:00:00";
        clearInterval(this.timer);
      }
    }, 1000);

    socket.on("endlessHighscores", this.handleHighscores);
    socket.on("endlessPlayerNameSaved", this.handleNameSaved);
  },

  beforeDestroy() {
    socket.off("endlessHighscores", this.handleHighscores);
    socket.off("endlessPlayerNameSaved", this.handleNameSaved);
  },

  methods: {
    handleNameSaved(payload) {
      if (!payload || payload.gameId !== window.localStorage.getItem("gameId")) return;
      if (typeof payload.name !== "string") return;

      const trimmed = payload.name.trim();
      window.localStorage.setItem("endlessPlayerName", trimmed);
      this.usernameLabel = trimmed || "Guest";

      const gameId = payload.gameId;
      const idx = this.topScores.findIndex((row) => row && row.game_id === gameId);
      if (idx >= 0) {
        const updated = { ...this.topScores[idx], name: trimmed };
        const next = [...this.topScores];
        next.splice(idx, 1, updated);
        this.topScores = next;
        return;
      }

      this.fetchTopScores();
    },

    copyScoreToClipboard() {
      const scoreString = `Lettergrid #${gameNumber()} - Score: ${this.score}`;

      navigator.clipboard.writeText(scoreString);

      this.isScoreCopied = true;
      setTimeout(() => {
        this.isScoreCopied = false;
      }, 3000);
    },

    tryAgain() {
      this.$store.commit("setActiveModal", "");
      this.$store.commit("endless/setIsGameOver", false);
      this.$store.commit("endless/setIsNextTileReady", true);
      this.$store.commit("endless/setRoundIndex", 0);

      window.localStorage.removeItem("gameId");
      const playerId = window.localStorage.getItem("playerId");
      const playerName = window.localStorage.getItem("endlessPlayerName");
      socket.emit("startEndless", playerId, null, playerName);
      this.fetchTopScores();
    },

    formatPlayerLabel(entry) {
      if (entry && typeof entry.name === "string" && entry.name.trim()) {
        return entry.name.trim();
      }

      const id = entry ? entry.player_id : null;
      if (!id) return "Guest";
      if (id.length <= 8) return id;
      return `${id.slice(0, 6)}…`;
    },

    formatHighscoreDate(value) {
      if (!value) return "";

      const dateTime = DateTime.fromJSDate(new Date(value));

      if (!dateTime.isValid) return "";

      return dateTime.toFormat("d LLL yyyy");
    },

    formatPlacement(value) {
      return `${value}${getOrdinalSuffix(value)}`;
    },

    scoreRowClasses(index) {
      if (index === 0) {
        return "bg-white bg-opacity-60 first-place text-lg";
      }

      if (index === 1) {
        return "bg-white bg-opacity-60 second-place";
      }

      if (index === 2) {
        return "bg-white bg-opacity-60 third-place";
      }

      return "bg-white bg-opacity-60 text-gray-700";
    },

    scoreRowStyles(index) {
      if (index === 0) {
        return { color: "#b8860b" };
      }

      if (index === 1) {
        return { color: "#a9a9a9" };
      }

      if (index === 2) {
        return { color: "#b87333" };
      }

      return {};
    },

    scoreLabelClasses(index) {
      if (index === 0) return "text-md font-semibold";
      return "text-sm text-current";
    },

    scoreValueClasses(index) {
      if (index === 0) return "text-sm text-current";
      if (index === 1) return "text-xs text-current";
      if (index === 2) return "text-xs text-current";
      return "text-xs text-current";
    },

    scoreDateStyles(index) {
      if (index === 0) return { opacity: 0.75 };
      if (index === 1) return { color: "#a9a9a9", opacity: 0.8 };
      if (index === 2) return { color: "#b87333", opacity: 0.8 };
      return { color: "#6b7280" };
    },

    fetchTopScores() {
      socket.emit("requestEndlessHighscores");
    },

    handleHighscores(scores) {
      if (Array.isArray(scores)) {
        this.topScores = scores;
        return;
      }

      this.topScores = [];
    },

    toIsoString(date) {
      let tzo = -date.getTimezoneOffset();
      let dif = tzo >= 0 ? "+" : "-";
      let pad = function (num) {
        return (num < 10 ? "0" : "") + num;
      };

      return (
        date.getFullYear() +
        "-" +
        pad(date.getMonth() + 1) +
        "-" +
        pad(date.getDate()) +
        "T" +
        pad(date.getHours()) +
        ":" +
        pad(date.getMinutes()) +
        ":" +
        pad(date.getSeconds()) +
        dif +
        pad(Math.floor(Math.abs(tzo) / 60)) +
        ":" +
        pad(Math.abs(tzo) % 60)
      );
    },
  },
};
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s 0.1s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

.first-place {
  background-size: 42px;
  background-color: #fdfff2;
  background-image: url("data:image/svg+xml,%3Csvg width='80' height='88' viewBox='0 0 80 88' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M22 21.91V26h-2c-9.94 0-18 8.06-18 18 0 9.943 8.058 18 18 18h2v4.09c8.012.722 14.785 5.738 18 12.73 3.212-6.99 9.983-12.008 18-12.73V62h2c9.94 0 18-8.06 18-18 0-9.943-8.058-18-18-18h-2v-4.09c-8.012-.722-14.785-5.738-18-12.73-3.212 6.99-9.983 12.008-18 12.73zM54 58v4.696c-5.574 1.316-10.455 4.428-14 8.69-3.545-4.262-8.426-7.374-14-8.69V58h-5.993C12.27 58 6 51.734 6 44c0-7.732 6.275-14 14.007-14H26v-4.696c5.574-1.316 10.455-4.428 14-8.69 3.545 4.262 8.426 7.374 14 8.69V30h5.993C67.73 30 74 36.266 74 44c0 7.732-6.275 14-14.007 14H54zM42 88c0-9.94 8.06-18 18-18h2v-4.09c8.016-.722 14.787-5.738 18-12.73v7.434c-3.545 4.262-8.426 7.374-14 8.69V74h-5.993C52.275 74 46 80.268 46 88h-4zm-4 0c0-9.943-8.058-18-18-18h-2v-4.09c-8.012-.722-14.785-5.738-18-12.73v7.434c3.545 4.262 8.426 7.374 14 8.69V74h5.993C27.73 74 34 80.266 34 88h4zm4-88c0 9.943 8.058 18 18 18h2v4.09c8.012.722 14.785 5.738 18 12.73v-7.434c-3.545-4.262-8.426-7.374-14-8.69V14h-5.993C52.27 14 46 7.734 46 0h-4zM0 34.82c3.213-6.992 9.984-12.008 18-12.73V18h2c9.94 0 18-8.06 18-18h-4c0 7.732-6.275 14-14.007 14H14v4.696c-5.574 1.316-10.455 4.428-14 8.69v7.433z' fill='%23ebeecd' fill-opacity='0.4' fill-rule='evenodd'/%3E%3C/svg%3E");
}
</style>



// WEBPACK FOOTER //
// src/components/modals/EndlessModal.vue